import React, { useState, useEffect, useRef } from 'react';
import moment from 'moment-timezone';
import '../styles.scss';
import {
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  CardContent,
  AccordionActions
} from '@mui/material';
import { SlideTransition } from '../../../../components/Transition/Transition';
import Grid from '@mui/material/Grid';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import './referralRecords.scss';
import Card from '@mui/material/Card';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Badge from '@mui/material/Badge';
import { ERROR, INIT, LOADING } from '../../../../constants/phase';
import { HOBBIES_TAG_STYLE } from '../../../../constants/my-details';
import { getLikedTagIntl } from '../../../../utils/my-details';
import { tryTranslate } from '../../../../utils/locale';
import AladdinHint from '../../../../components/AladdinHint/AladdinHint';
import { getUserTimezone } from '../../../../utils/user';
import SelectButtonGroup from '../../../../components/SelectButtonGroup/SelectButtonGroup';
import { usePrevious } from '../../../../utils/hooks';
import ReferralExistDialog from './ReferralExistDialog/ReferralExistDialog';
import { getReferralSourceString } from '../../../../utils/referral';
import { REFERRAL_SOURCE } from '../../../../constants/referral';
import { clientPath } from '../../../../utils/client-route';

const LinkStyle = { textDecoration: 'underline', color: 'inherit' };

const ReferralListItem = props => (
  <ListItem divider={props.divider || true}>
    <Grid
      container
      spacing={1}
      sx={{
        alignItems: props.alignItems || 'flex-start'
      }}
    >
      <Grid size={{ xs: 3, sm: 2, md: 1 }}>{props.title}</Grid>
      <Grid size={{ xs: 9, sm: 10, md: 11 }} style={{ textAlign: 'center' }}>
        {props.content}
      </Grid>
    </Grid>
  </ListItem>
);

const ReferralActionableValue = props => (
  <Grid
    container
    wrap="nowrap"
    sx={{
      alignItems: 'center',
      justifyContent: 'center'
    }}
  >
    <Grid style={{ width: 'calc(100% - 80px)' }}>{props.value}</Grid>
    {props.action}
  </Grid>
);

const ReferralRecordView = props => {
  const {
    intl,
    history,
    userDetails,
    referralRecords,
    clientData,
    fetchClientPhase,
    deleteClientPhase,
    deletePhase,
    clearClientDetails,
    deleteRecord,
    updateReferralById,
    getReferralRecords,
    getClient,
    renderHeaderButton,
    handleOpenSubscriptionDialog,
    disableAddClient
  } = props;

  const [selectedId, setSelectedId] = useState(undefined);
  const [deleteId, setDeleteId] = useState(undefined);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [referralExistDialog, setReferralExistDialog] = useState(false);
  const referralRef = useRef(null);
  const prevDeletePhase = usePrevious(deletePhase);

  const addClient = referral => {
    const isInClientList = referral.email && (clientData || []).find(client => client.email === referral.email);

    if (isInClientList) {
      // do not pass email to client page if the email is already in the client list
      const newReferral = { ...referral, email: '' };
      referralRef.current = newReferral;
      setReferralExistDialog(true);
    } else {
      referralRef.current = referral;
      proceedAddClient();
    }
  };

  const closeReferralExistDialog = () => setReferralExistDialog(false);
  const proceedAddClient = () => {
    if (disableAddClient) {
      handleOpenSubscriptionDialog();
    } else {
      localStorage.removeItem('clientId');
      localStorage.removeItem('arrowRotateClient');
      sessionStorage.setItem('referralRecordId', referralRef.current._id);
      clearClientDetails();
      history.push({
        state: { referralRecord: referralRef.current },
        pathname: '/client_personal_form'
      });
    }
  };

  const openDeleteDialog = referralId => {
    setDeleteId(referralId);
    setDeleteDialog(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialog(false);
  };

  const deleteReferral = () => {
    deleteRecord(deleteId);
    setDeleteDialog(false);
  };

  // const clientMessages = (referral) => {
  // 	var contactNo = _.get(referral, 'whatsapp', '');
  // 	let phoneNoText = contactNo ? 'phone=' + contactNo.replace('+', '') : '';
  // 	let url = 'https://api.whatsapp.com/send?' + phoneNoText + '&text=Hi, we are connected in PortfoPlus';
  // 	var win = window.open(url, '_blank');
  // 	if (win) win.focus();
  // 	// localStorage.setItem('clientId', JSON.stringify(referral._id));
  // 	// this.props.history.push({
  // 	//   pathname: '/adviser_message'
  // 	// });
  // }

  const selectReferral = (event, expanded, referral) => {
    setSelectedId(selectedId === referral._id ? undefined : referral._id);
    // update isNewClient status if the referral is new
    if (referral.isNewClient) {
      updateReferralById(referral._id, { isNewClient: false });
    }
  };

  const lazyPackIntl = intl.formatMessage({ id: 'lazy-pack' });

  useEffect(() => {
    getReferralRecords();
  }, [getReferralRecords]);

  // The client list is only read here for the "already a client" warning and to
  // resolve referrer names, and login/boot already fetches it. getClient is the
  // heaviest adviser query in the app, so ask for it only when the session has
  // none — never fetched, or the last attempt failed. Clients ADDED during the
  // session are merged in by the store's own GET_CLIENT_BY_ID refresh.
  //
  // The ref is what bounds that to ONE attempt per mount, and it is load-bearing
  // rather than tidiness: GET_CLIENT_ERROR sets fetchClientPhase straight back
  // to ERROR (store/client/duck.js:331), which re-satisfies this same branch, so
  // without it a persistently failing endpoint — offline on the app, or the
  // 502/503 a load balancer returns during a rolling deploy — would re-dispatch
  // with no backoff for as long as the page stays mounted, and every looping
  // client would then hit the server together the moment it came back. The epic
  // is a bare `mergeMap` (duck.js:900), so nothing downstream dedups or
  // throttles. One attempt per mount is also what this page did before the
  // guard existed, so a genuinely failed session still recovers on the next
  // visit.
  //
  // Clients DELETED during the session are a separate case: DELETE_CLIENT_SUCCESS
  // (store/client/duck.js:390) only sets a phase, it never prunes clientData.
  // Without the extra branch below, a client deleted this session still matches
  // `isInClientList` above — so 加為客戶 on that person's referral would raise
  // ReferralExistDialog and strip the email off the new-client form. That is a
  // false positive, so checkClient-on-submit does not cover it: it never runs.
  // One refetch is enough; the flag stays true until INIT_PHASE resets it, and
  // the ref stops it re-firing on the resulting phase changes.
  const requestedClientListRef = useRef(false);
  const refetchedAfterDeleteRef = useRef(false);
  useEffect(() => {
    if (fetchClientPhase === LOADING) return;
    if (fetchClientPhase === INIT || fetchClientPhase === ERROR) {
      if (!requestedClientListRef.current) {
        requestedClientListRef.current = true;
        getClient();
      }
      return;
    }
    if (deleteClientPhase === true && !refetchedAfterDeleteRef.current) {
      refetchedAfterDeleteRef.current = true;
      getClient();
    }
  }, [fetchClientPhase, deleteClientPhase, getClient]);

  useEffect(() => {
    renderHeaderButton(false);
  }, [renderHeaderButton]);

  useEffect(() => {
    if (prevDeletePhase !== true && deletePhase === true) {
      getReferralRecords();
    }
  }, [prevDeletePhase, deletePhase, getReferralRecords]);

  return (
    <div>
      <ReferralExistDialog open={referralExistDialog} onClose={closeReferralExistDialog} onOK={proceedAddClient} />
      <Dialog
        open={deleteDialog}
        onClose={closeDeleteDialog}
        aria-labelledby="alert-dialog-slide-title"
        aria-describedby="alert-dialog-slide-description"
        fullWidth={true}
        slots={{
          transition: SlideTransition
        }}
      >
        <DialogTitle id="alert-dialog-slide-title">{intl.formatMessage({ id: 'Delete Record' })}</DialogTitle>
        <DialogContent>
          {intl.formatMessage({
            id: 'Are you sure you want to delete this?'
          })}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} color="primary">
            {intl.formatMessage({ id: 'Cancel' })}
          </Button>
          <Button onClick={deleteReferral} color="primary" autoFocus>
            {intl.formatMessage({ id: 'OK' })}
          </Button>
        </DialogActions>
      </Dialog>
      <div id="wrapper-referrel" className="referral__page referral-records-page">
        <div id="page-content-wrapper">
          <div className="container-fluid">
            <div className="row">
              <div className="col-lg-12">
                <div className="reminder-box">
                  <Grid
                    container
                    sx={{
                      justifyContent: 'center'
                    }}
                  >
                    <Grid>
                      <AladdinHint
                        containerProps={{ justify: 'center' }}
                        title={intl.formatMessage({ id: 'aladdin-referral-records-title' })}
                      />
                    </Grid>

                    <Grid size={12}>
                      <Card className="referral-header" style={{ boxShadow: 'none' }}>
                        <CardContent style={{ padding: 16 }}>
                          <Grid
                            container
                            sx={{
                              alignItems: 'center'
                            }}
                          >
                            <Grid size={{ xs: 8, sm: 7 }}>{intl.formatMessage({ id: 'New client' })}</Grid>
                            <Grid size={{ xs: 4, sm: 5 }}>{intl.formatMessage({ id: 'source' })}</Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>

                    {referralRecords &&
                      referralRecords.map((referral, index) => {
                        const sourceStr = getReferralSourceString(intl, referral);

                        let referByName = '';
                        let _id = '';
                        let luckyArray = [];
                        if (referral.referBy && !referral.referUserFactsheetId) {
                          if (clientData && clientData.length > 0) {
                            // eslint-disable-next-line
                            clientData.map(val1 => {
                              if (referral.referBy === val1.email) {
                                referByName =
                                  val1.factsheetId && val1.factsheetId.name ? val1.factsheetId.name : val1.name;
                                _id = val1._id;
                                luckyArray.push(val1);
                              }
                            });
                          }
                        }
                        if (referral.referUserId && referral.referUserFactsheetId) {
                          referByName =
                            referral.referUserFactsheetId && referral.referUserFactsheetId.name
                              ? referral.referUserFactsheetId.name
                              : '';
                          _id = referral.referUserId;
                          //luckyArray.push(referral.referUserId);
                          if (clientData && clientData.length > 0) {
                            // eslint-disable-next-line
                            clientData.map(val1 => {
                              if (referral.referUserId === val1._id) {
                                referByName =
                                  val1.factsheetId && val1.factsheetId.name ? val1.factsheetId.name : val1.name;
                                _id = val1._id;
                                luckyArray.push(val1);
                              }
                            });
                          }
                        }
                        let showEmail = true;
                        let referralId = _id.toString();
                        if (referral.referBy && !referral.referUserFactsheetId) {
                          let inactiveEmail = referral.referBy ? referral.referBy : '';
                          if (inactiveEmail.indexOf(referralId) !== -1) {
                            showEmail = false;
                          }
                        }
                        if (referral.referUserId && referral.referUserFactsheetId) {
                          let inactiveEmail = referral.referUserFactsheetId.email
                            ? referral.referUserFactsheetId.email
                            : '';
                          if (inactiveEmail.indexOf(referralId) !== -1) {
                            showEmail = false;
                          }
                        }
                        let isObj = false;
                        if (referral.referUserId && referral.referUserFactsheetId) {
                          isObj = true;
                        }

                        const whoText =
                          referral.source === REFERRAL_SOURCE.LAZY_PACK
                            ? lazyPackIntl
                            : referral.source === REFERRAL_SOURCE.HOT_TOPIC_STUDIO
                            ? intl.formatMessage({ id: 'hot-topic-studio-title' })
                            : referral.source === REFERRAL_SOURCE.CUSTOM_NEWSFEED
                            ? intl.formatMessage({ id: 'selected-newsfeed-nav-title' })
                            : showEmail && referral.referUserFactsheetId
                            ? referral.referUserFactsheetId.email
                            : referByName;

                        const showWho = () => {
                          history.push({
                            pathname: clientPath(),
                            select: 'particular',
                            preSelectedClientIds: luckyArray.map(client => client._id)
                          });
                        };

                        const wechatId =
                          referral.wechatId && referral.wechatId.length > 0 ? referral.wechatId : undefined;

                        return (
                          <Grid size={12} key={index}>
                            <Accordion
                              id={referral._id}
                              className="referral-record"
                              expanded={selectedId === referral._id}
                              onChange={(event, expanded) => selectReferral(event, expanded, referral)}
                            >
                              <AccordionSummary
                                className="summary"
                                style={{ padding: '0 48px 0 16px' }}
                                expandIcon={<ExpandMoreIcon color="primary" />}
                              >
                                <Grid
                                  container
                                  spacing={1}
                                  sx={{
                                    alignItems: 'center'
                                  }}
                                >
                                  <Grid size={{ xs: 8, sm: 7 }}>
                                    <Grid
                                      container
                                      spacing={1}
                                      wrap="nowrap"
                                      sx={{
                                        alignItems: 'center'
                                      }}
                                    >
                                      <Grid>
                                        {!referral.name && referral.isAutoGenerated
                                          ? intl.formatMessage({ id: 'referral-records-auto-gen' })
                                          : referral.name}
                                      </Grid>
                                      {!!referral.isNewClient && (
                                        <Grid>
                                          <Badge
                                            className="Badge todo-badge"
                                            badgeContent={intl.formatMessage({ id: 'New' })}
                                            color="error"
                                          />
                                        </Grid>
                                      )}
                                    </Grid>
                                  </Grid>
                                  <Grid size={{ xs: 4, sm: 5 }}>
                                    <div>{sourceStr}</div>
                                    {/*<div>{referral.source === 'lazy-pack' ? lazyPackIntl : referByName || 'N.A.'}</div>*/}
                                  </Grid>
                                </Grid>
                              </AccordionSummary>
                              <AccordionDetails className="details" style={{ flexDirection: 'column' }}>
                                <List style={{ width: '100%' }}>
                                  <ReferralListItem
                                    title={`${intl.formatMessage({ id: 'Tel' })}:`}
                                    content={
                                      referral.phoneNumber && (
                                        <a href={'tel:' + referral.phoneNumber} target="_top" style={LinkStyle}>
                                          {referral.phoneNumber}
                                        </a>
                                      )
                                    }
                                  />

                                  <ReferralListItem
                                    title={`${intl.formatMessage({ id: 'Email' })}:`}
                                    content={
                                      referral.email && (
                                        <a href={'mailto:' + referral.email} target="_top" style={LinkStyle}>
                                          {referral.email}
                                        </a>
                                      )
                                    }
                                  />
                                  {wechatId && (
                                    <ReferralListItem
                                      title={`${intl.formatMessage({ id: 'WeChat' })}:`}
                                      content={wechatId}
                                    />
                                  )}

                                  {referral.answerTags &&
                                    Object.keys(referral.answerTags).map((question, index) => (
                                      <ReferralListItem
                                        alignItems="center"
                                        title={`${intl.formatMessage(
                                          { id: 'question-number' },
                                          { number: index + 1 }
                                        )}:`}
                                        content={
                                          <Grid
                                            container
                                            spacing={1}
                                            sx={{
                                              flexDirection: 'column',
                                              alignItems: 'center',
                                              justifyContent: 'center'
                                            }}
                                          >
                                            <Grid>
                                              {question}
                                              <SelectButtonGroup
                                                sx={{ justifyContent: 'center' }}
                                                multi={true}
                                                choices={referral.answerTags[question].map(answer => ({
                                                  value: answer,
                                                  label: answer,
                                                  style: HOBBIES_TAG_STYLE,
                                                  disableRipple: true,
                                                  disableFocusRipple: true
                                                }))}
                                                value={[]}
                                              />
                                            </Grid>
                                          </Grid>
                                        }
                                      />
                                    ))}

                                  <ReferralListItem
                                    alignItems="center"
                                    title={`${intl.formatMessage({ id: 'referral-request' })}:`}
                                    content={
                                      <Grid
                                        container
                                        spacing={1}
                                        sx={{ flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
                                      >
                                        <Grid>
                                          <SelectButtonGroup
                                            sx={{ justifyContent: 'center' }}
                                            multi={true}
                                            choices={(referral.likedTags || []).map(likedTag => ({
                                              value: likedTag,
                                              label: tryTranslate(intl, getLikedTagIntl(likedTag)),
                                              style: HOBBIES_TAG_STYLE,
                                              disableRipple: true,
                                              disableFocusRipple: true
                                            }))}
                                            value={[]}
                                          />
                                        </Grid>

                                        {/*{(referral.likedTags || []).map(likedTag => (*/}
                                        {/*  <Grid key={likedTag}>*/}
                                        {/*    <AdviserInfoTagChip*/}
                                        {/*      isIntoView={true}*/}
                                        {/*      color={THEME_COLOR_PALETTES['blue'][1]}*/}
                                        {/*      label={tryTranslate(intl, getLikedTagIntl(likedTag))}*/}
                                        {/*    />*/}
                                        {/*  </Grid>*/}
                                        {/*))}*/}
                                      </Grid>
                                    }
                                  />

                                  <ReferralListItem
                                    alignItems="center"
                                    title={`${intl.formatMessage({ id: 'leave-comment' })}:`}
                                    content={
                                      <Grid
                                        container
                                        spacing={1}
                                        sx={{ flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
                                      >
                                        {referral.helpItems && (
                                          <Grid>
                                            <SelectButtonGroup
                                              sx={{ justifyContent: 'center' }}
                                              multi={true}
                                              choices={(referral.helpItems || []).map(helpItem => ({
                                                value: helpItem,
                                                label: tryTranslate(intl, getLikedTagIntl(helpItem)),
                                                style: HOBBIES_TAG_STYLE,
                                                disableRipple: true,
                                                disableFocusRipple: true
                                              }))}
                                              value={[]}
                                            />

                                            {/*{(referral.helpItems || []).map(helpItem => (*/}
                                            {/*  <Grid key={helpItem}>*/}
                                            {/*    <AdviserInfoTagChip*/}
                                            {/*      isIntoView={true}*/}
                                            {/*      color={THEME_COLOR_PALETTES['blue'][1]}*/}
                                            {/*      label={tryTranslate(intl, getHelpItemIntl(helpItem))}*/}
                                            {/*    />*/}
                                            {/*  </Grid>*/}
                                            {/*))}*/}
                                          </Grid>
                                        )}
                                        {referral.remark && <Grid>{referral.remark || ''}</Grid>}
                                      </Grid>
                                    }
                                  />

                                  {/*<ReferralListItem*/}
                                  {/*  title={`${intl.formatMessage({ id: 'WhatsApp' })}:`}*/}
                                  {/*  content={*/}
                                  {/*    <ReferralActionableValue*/}
                                  {/*      value={referral.whatsapp}*/}
                                  {/*      action={*/}
                                  {/*        referral.whatsapp && (*/}
                                  {/*          <Grid className="action">*/}
                                  {/*            <button*/}
                                  {/*              className="list-link-btn"*/}
                                  {/*              onClick={this.clientMessages.bind(this, referral)}*/}
                                  {/*            >*/}
                                  {/*              {intl.formatMessage({ id: 'Go' })}*/}
                                  {/*            </button>*/}
                                  {/*          </Grid>*/}
                                  {/*        )*/}
                                  {/*      }*/}
                                  {/*    />*/}
                                  {/*  }*/}
                                  {/*/>*/}

                                  {/*<ReferralListItem*/}
                                  {/*  title={`${intl.formatMessage({ id: 'WeChat' })}:`}*/}
                                  {/*  content={referral.wechatId}*/}
                                  {/*/>*/}

                                  {/*<ReferralListItem*/}
                                  {/*  title={`${intl.formatMessage({ id: 'Line ID' })}:`}*/}
                                  {/*  content={referral.lineId}*/}
                                  {/*/>*/}

                                  <ReferralListItem
                                    title={`${intl.formatMessage({ id: 'Refer By' })}:`}
                                    content={
                                      <ReferralActionableValue
                                        value={
                                          referral.referBy ||
                                          (referral.referUserId && referral.referUserFactsheetId) ? (
                                            <span style={LinkStyle} onClick={showWho}>
                                              {whoText}
                                            </span>
                                          ) : (
                                            whoText
                                          )
                                        }
                                      />
                                    }
                                  />

                                  <ReferralListItem
                                    title={`${intl.formatMessage({ id: 'Date' })}:`}
                                    content={moment(referral.createdate)
                                      .utcOffset(getUserTimezone(userDetails))
                                      .format('ddd DD-MMM-YYYY HH:mm')}
                                  />

                                  <ReferralListItem
                                    title={`${intl.formatMessage({ id: 'source' })}:`}
                                    content={sourceStr}
                                    divider={false}
                                  />
                                </List>
                              </AccordionDetails>
                              <AccordionActions>
                                <Grid
                                  container
                                  sx={{
                                    justifyContent: 'space-between'
                                  }}
                                >
                                  <Grid>
                                    <Button
                                      variant="outlined"
                                      color="primary"
                                      onClick={() => openDeleteDialog(referral._id)}
                                    >
                                      {intl.formatMessage({ id: 'Delete' })}
                                    </Button>
                                  </Grid>
                                  <Grid>
                                    <Button variant="contained" color="primary" onClick={() => addClient(referral)}>
                                      {intl.formatMessage({ id: 'referral-add' })}
                                    </Button>
                                  </Grid>
                                </Grid>
                              </AccordionActions>
                            </Accordion>
                          </Grid>
                        );
                      })}
                  </Grid>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReferralRecordView;
